/**
 * Email Variable Type Definitions
 *
 * Defines all personalization variables available in the email editor.
 * Variables use double-brace format: {{variable}}
 *
 * Phase 1, Task 1.1
 * Date: 2025-10-21
 */

import { User, Mail, Phone, ShoppingCart, DollarSign, Calendar, Building, Link, Tag, Unlink } from 'lucide-react';

/**
 * Email variable category types
 * Used to organize variables in the UI
 */
export type EmailVariableCategory =
  | 'customer'      // Customer personal data (firstName, email, etc.)
  | 'activity'      // Customer activity data (totalOrders, totalSpend, etc.)
  | 'restaurant'    // Restaurant static data (restaurantName, etc.)
  | 'dynamic'       // Dynamic restaurant URLs (resolved at send time)
  | 'campaign'      // Campaign-specific variables (promoCode, etc.)
  | 'system';       // System variables (unsubscribeUrl, etc.)

/**
 * Email variable definition interface
 */
export interface EmailVariable {
  /** Display name shown in UI */
  name: string;

  /** Template variable with {{braces}} */
  variable: string;

  /** Human-readable description */
  description: string;

  /** Variable category for organization */
  category: EmailVariableCategory;

  /** Example value to show users */
  example?: string;

  /** Icon component for UI display */
  icon?: React.ComponentType<{ className?: string }>;

  /** Whether this variable is required (e.g., unsubscribeUrl) */
  required?: boolean;

  /** Additional notes or warnings */
  note?: string;
}

/**
 * Complete list of available email variables
 * Organized by category for easy access
 */
export const EMAIL_VARIABLES: Record<EmailVariableCategory, EmailVariable[]> = {
  /**
   * CUSTOMER VARIABLES
   * Personal information about the customer
   */
  customer: [
    {
      name: 'First Name',
      variable: '{{firstName}}',
      description: 'Customer\'s first name',
      category: 'customer',
      example: 'John',
      icon: User
    },
    {
      name: 'Last Name',
      variable: '{{lastName}}',
      description: 'Customer\'s last name',
      category: 'customer',
      example: 'Smith',
      icon: User
    },
    {
      name: 'Full Name',
      variable: '{{fullName}}',
      description: 'Customer\'s full name (first + last)',
      category: 'customer',
      example: 'John Smith',
      icon: User
    },
    {
      name: 'Email',
      variable: '{{email}}',
      description: 'Customer\'s email address',
      category: 'customer',
      example: 'john@example.com',
      icon: Mail
    },
    {
      name: 'Phone',
      variable: '{{phone}}',
      description: 'Customer\'s phone number',
      category: 'customer',
      example: '+64 21 123 4567',
      icon: Phone
    }
  ],

  /**
   * ACTIVITY VARIABLES
   * Customer order history and engagement data
   */
  activity: [
    {
      name: 'Total Orders',
      variable: '{{totalOrders}}',
      description: 'Total number of orders placed',
      category: 'activity',
      example: '15',
      icon: ShoppingCart
    },
    {
      name: 'Total Spend',
      variable: '{{totalSpend}}',
      description: 'Total amount spent across all orders',
      category: 'activity',
      example: '$450.00',
      icon: DollarSign
    },
    {
      name: 'Latest Order Date',
      variable: '{{latestOrderDate}}',
      description: 'Date of most recent order',
      category: 'activity',
      example: '2025-10-01',
      icon: Calendar
    }
  ],

  /**
   * RESTAURANT VARIABLES (Static)
   * Restaurant/organization branding information
   */
  restaurant: [
    {
      name: 'Restaurant Name',
      variable: '{{restaurantName}}',
      description: 'Name of the restaurant',
      category: 'restaurant',
      example: 'Example Restaurant',
      icon: Building
    },
    {
      name: 'Organisation Name',
      variable: '{{organisationName}}',
      description: 'Name of the parent organization',
      category: 'restaurant',
      example: 'Example Restaurant Group',
      icon: Building
    }
  ],

  /**
   * DYNAMIC VARIABLES
   * Restaurant URLs that resolve to customer's preferred restaurant
   * Uses 3-tier fallback: customer's last restaurant → org defaults → rule restaurant
   */
  dynamic: [
    {
      name: 'Ordering Page URL',
      variable: '{{preferredRestaurantOrderingUrl}}',
      description: 'Link to customer\'s preferred restaurant ordering page',
      category: 'dynamic',
      icon: ShoppingCart,
      note: 'Resolves to customer\'s last restaurant at send time'
    },
    {
      name: 'Review Page URL',
      variable: '{{preferredRestaurantReviewUrl}}',
      description: 'Link to leave a review for customer\'s preferred restaurant',
      category: 'dynamic',
      icon: Link,
      note: 'Resolves to customer\'s last restaurant at send time'
    },
    {
      name: 'Instagram URL',
      variable: '{{preferredRestaurantInstagramUrl}}',
      description: 'Instagram profile of customer\'s preferred restaurant',
      category: 'dynamic',
      icon: Link,
      note: 'Resolves to customer\'s last restaurant at send time'
    },
    {
      name: 'Facebook URL',
      variable: '{{preferredRestaurantFacebookUrl}}',
      description: 'Facebook page of customer\'s preferred restaurant',
      category: 'dynamic',
      icon: Link,
      note: 'Resolves to customer\'s last restaurant at send time'
    },
    {
      name: 'Google Review URL',
      variable: '{{preferredRestaurantGoogleReviewUrl}}',
      description: 'Google review link for customer\'s preferred restaurant',
      category: 'dynamic',
      icon: Link,
      note: 'Resolves to customer\'s last restaurant at send time'
    }
  ],

  /**
   * CAMPAIGN VARIABLES
   * Campaign-specific data (promo codes, offers, etc.)
   */
  campaign: [
    {
      name: 'Promo Code',
      variable: '{{promoCode}}',
      description: 'Promotional code for this campaign',
      category: 'campaign',
      example: 'SAVE20',
      icon: Tag,
      note: 'Insert actual promo code value, not this variable'
    },
    {
      name: 'Promo Code Expiry',
      variable: '{{promoCodeExpiry}}',
      description: 'Expiration date of the promo code',
      category: 'campaign',
      example: '2025-12-31',
      icon: Calendar
    }
  ],

  /**
   * SYSTEM VARIABLES
   * Required or system-generated variables
   */
  system: [
    {
      name: 'Unsubscribe URL',
      variable: '{{unsubscribeUrl}}',
      description: 'Link to unsubscribe from emails',
      category: 'system',
      example: 'https://app.pumpd.co.nz/unsubscribe/...',
      icon: Unlink,
      required: true,
      note: 'Required for CAN-SPAM compliance'
    },
    {
      name: 'Current Year',
      variable: '{{currentYear}}',
      description: 'Current year (for footer copyright)',
      category: 'system',
      example: '2025',
      icon: Calendar
    }
  ]
};

/**
 * Get all variables as a flat array
 */
export function getAllVariables(): EmailVariable[] {
  return Object.values(EMAIL_VARIABLES).flat();
}

/**
 * Get variables by category
 */
export function getVariablesByCategory(category: EmailVariableCategory): EmailVariable[] {
  return EMAIL_VARIABLES[category] || [];
}

/**
 * Get a specific variable by its template string
 */
export function getVariableByTemplate(template: string): EmailVariable | undefined {
  return getAllVariables().find(v => v.variable === template);
}

/**
 * Check if a string is a known variable
 */
export function isKnownVariable(variable: string): boolean {
  return getAllVariables().some(v => v.variable === variable);
}

/**
 * Check if a variable is dynamic (requires runtime resolution)
 */
export function isDynamicVariable(variable: string): boolean {
  return EMAIL_VARIABLES.dynamic.some(v => v.variable === variable);
}

/**
 * Check if a variable is required
 */
export function isRequiredVariable(variable: string): boolean {
  const varDef = getVariableByTemplate(variable);
  return varDef?.required === true;
}

/**
 * Get all required variables
 */
export function getRequiredVariables(): EmailVariable[] {
  return getAllVariables().filter(v => v.required);
}

/**
 * Get variable category
 */
export function getVariableCategory(variable: string): EmailVariableCategory | null {
  const varDef = getVariableByTemplate(variable);
  return varDef?.category || null;
}

/**
 * Check if a variable or string is a valid URL (for button hrefs)
 * Returns true for:
 * - All dynamic variables ({{preferredRestaurant...Url}})
 * - System URL variables ({{unsubscribeUrl}}, {{viewInBrowserUrl}}) (VIEW IN BROWSER URL REMOVED FOR NOW)
 * - Static URLs (strings starting with http:// or https://)
 *
 * Returns false for:
 * - Customer data variables ({{firstName}}, {{email}}, etc.)
 * - Activity variables ({{totalOrders}}, etc.)
 * - Restaurant name variables ({{restaurantName}}, etc.)
 * - Campaign variables ({{promoCode}}, etc.)
 * - Non-URL system variables ({{currentYear}})
 */
export function isUrlVariable(variable: string): boolean {
  // Check if it's a static URL (not a variable)
  if (/^https?:\/\//i.test(variable)) {
    return true;
  }

  // Check if it's a known variable
  const varDef = getVariableByTemplate(variable);
  if (!varDef) {
    return false; // Unknown variable
  }

  // All dynamic variables are URLs
  if (varDef.category === 'dynamic') {
    return true;
  }

  // System category: only URL-type variables
  if (varDef.category === 'system') {
    return variable === '{{unsubscribeUrl}}'; // VIEW IN BROWSER URL REMOVED FOR NOW
  }

  // All other categories (customer, activity, restaurant, campaign) are not URLs
  return false;
}

/**
 * Get all URL-type variables (suitable for button hrefs)
 */
export function getUrlVariables(): EmailVariable[] {
  return getAllVariables().filter(v => isUrlVariable(v.variable));
}

/**
 * Format variable for display (remove braces)
 */
export function formatVariableForDisplay(variable: string): string {
  return variable.replace(/{{|}}/g, '');
}

/**
 * Category display names for UI
 */
export const CATEGORY_LABELS: Record<EmailVariableCategory, string> = {
  customer: 'Customer Info',
  activity: 'Customer Activity',
  restaurant: 'Restaurant Info',
  dynamic: 'Dynamic Restaurant URLs',
  campaign: 'Campaign Data',
  system: 'System Variables'
};

/**
 * Category descriptions for tooltips
 */
export const CATEGORY_DESCRIPTIONS: Record<EmailVariableCategory, string> = {
  customer: 'Personal information about the customer',
  activity: 'Customer order history and engagement metrics',
  restaurant: 'Restaurant and organization branding',
  dynamic: 'Restaurant-specific URLs that resolve to customer\'s preferred location',
  campaign: 'Campaign-specific offers and promotions',
  system: 'System-generated links and data'
};

/**
 * Total count of variables
 */
export const TOTAL_VARIABLES = getAllVariables().length;

/**
 * Export for debugging/logging
 */
export const VARIABLE_STATS = {
  total: TOTAL_VARIABLES,
  byCategory: {
    customer: EMAIL_VARIABLES.customer.length,
    activity: EMAIL_VARIABLES.activity.length,
    restaurant: EMAIL_VARIABLES.restaurant.length,
    dynamic: EMAIL_VARIABLES.dynamic.length,
    campaign: EMAIL_VARIABLES.campaign.length,
    system: EMAIL_VARIABLES.system.length
  },
  required: getRequiredVariables().length
};
